import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { Button } from "@progress/kendo-react-buttons";
import { filterBy } from "@progress/kendo-data-query";
import { chevronDownIcon } from "@progress/kendo-svg-icons";
import { toast } from "react-toastify";

import { DateFormat } from "components/common/formatter";
import SubmitButton from "components/common/SubmitButton";
import { Tile } from "components/UIComponents/Tile";
import { useActiveFeeEarner } from "services/userService";
import { allocateTasks } from "services/tasksService";

import styles from "./Tasks.module.scss";

const getDisplayStatus = (item) => {
  if (item?.completed) return "Completed";
  if (item?.isOverdue) return "Overdue";
  if (item?.isDueToday) return "Due";
  return "Pending";
};

const TasksAllocationModal = ({ tasks = [], onClose, onSuccess }) => {
  const { data: feeEarnerData, loading } = useActiveFeeEarner();
  const [feeEarnerList, setFeeEarnerList] = useState([]);
  const [selectedFeeEarner, setSelectedFeeEarner] = useState(null);
  const [feeEarnerError, setFeeEarnerError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (feeEarnerData?.length) {
      setFeeEarnerList(feeEarnerData);
    }
  }, [feeEarnerData]);

  const gridData = useMemo(
    () => ({ data: tasks, total: tasks.length }),
    [tasks],
  );

  const DateCell = (props) => {
    const dateField = props.dataItem[props.field];
    return (
      <td className={props.className}>
        {dateField ? DateFormat(dateField) : ""}
      </td>
    );
  };

  const StatusCell = (props) => {
    const label = getDisplayStatus(props.dataItem);
    const tone = label.toLowerCase();
    return (
      <td className={props.className}>
        <span className={`${styles.statusPill} ${styles[`status-${tone}`]}`}>
          {label}
        </span>
      </td>
    );
  };

  const filterFeeEarner = (filter) => {
    const data = feeEarnerData?.slice() || [];
    return filterBy(data, filter);
  };

  const handleAllocate = async () => {
    const taskIds = tasks
      .map((t) => Number(t.taskId))
      .filter((id) => Number.isFinite(id));
    const feeEarnerRef = selectedFeeEarner?.userRef;

    if (!taskIds.length) {
      toast.error("No tasks selected");
      return;
    }
    if (!feeEarnerRef) {
      setFeeEarnerError(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await allocateTasks(taskIds, feeEarnerRef);
      if (res.status === 200 || res.status === 204) {
        toast.success(
          taskIds.length > 1
            ? `${taskIds.length} tasks allocated successfully`
            : "Task allocated successfully",
        );
        onSuccess?.();
        onClose?.();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      title="Allocation"
      onClose={onClose}
      className={`dialog-md ${styles.allocationDialog}`}
    >
      <div className="d-flex flex-column flex-grow-1 min-h-0 h-100 p-3 px-4 gap-3">
        <Tile
          heading="Selected Tasks"
          className="d-flex flex-column flex-grow-1 min-h-0 overflow-hidden px-3 pb-3"
        >
          <div className={styles.allocationFlexFill}>
            <Grid
              data={gridData}
              className="fs-md h-100"
              style={{ height: "100%" }}
            >
              <GridColumn field="date" title="Date" cell={DateCell} width="110px" />
              <GridColumn field="matterRef" title="Matter Ref" width="130px" />
              <GridColumn field="details" title="Details" />
              <GridColumn
                field="addedByUserRef"
                title="Created by"
                width="110px"
              />
              <GridColumn
                field="dateDueBy"
                title="Due Date"
                cell={DateCell}
                width="110px"
              />
              <GridColumn
                field="status"
                title="Status"
                cell={StatusCell}
                width="110px"
              />
            </Grid>
          </div>
        </Tile>

        <Tile heading="Allocate to" className="flex-shrink-0 px-3 pb-3">
          <div
            className={feeEarnerError ? "validation-invalid" : ""}
            style={{ maxWidth: "320px" }}
          >
            <Label className="filter-font mb-1">Fee Earner</Label>
            <ComboBox
              style={{ width: "100%", height: "40px" }}
              data={feeEarnerList}
              textField="display"
              dataItemKey="userRef"
              filterable={true}
              svgIcon={chevronDownIcon}
              loading={loading}
              value={selectedFeeEarner}
              onChange={(e) => {
                setSelectedFeeEarner(e.value);
                if (e.value?.userRef) setFeeEarnerError(false);
              }}
              onFilterChange={(e) =>
                setFeeEarnerList(filterFeeEarner(e.filter))
              }
              placeholder="Select Fee Earner"
            />
          </div>
        </Tile>
      </div>

      <DialogActionsBar>
        <div className="dialog-footer w-100 d-flex justify-content-end gap-2 pe-3">
          <Button
            className="common-btn bgBtnSecondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <SubmitButton onClick={handleAllocate} isLoading={isSubmitting}>
            Allocate
          </SubmitButton>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default TasksAllocationModal;
